import React from "react";
import { useMsal } from "@azure/msal-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchQAFeedbackForProject } from './qaFeedbackService';
import TopNav from "../TopNav/TopNav";
import JobNav from "../../components/JobDetailPage/JobNav";
import JobGeneralOverview from "../../components/JobDetailPage/JobOverviewBar";
import { selectJobId, selectGeneralProject } from "../../redux/selectors";
import { useSelector } from "react-redux";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import axios from "axios";
import useJobHandler from "../../functions/JobHandler";

const JobQa = () => {
  const loadRequest = useRef(0);
  const [loadError, setLoadError] = useState('');
  const [matchingItems, setMatchingItems] = useState([]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]); // Change to an array to handle multiple files
  const [attachments, setAttachments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { instance } = useMsal();
  const jobId = useSelector(selectJobId);
  const projectDetails = useSelector(selectGeneralProject);
  const jobNumber = projectDetails[0]?.JobNumber || "";
  

    useJobHandler();

  const handleClose = () => setShowModal(false);

  const handleShow = async (item) => {
    setSelectedItem(item); // Set selected item first
    setShowModal(true);
    setAttachments([]); // Reset attachments to avoid undefined errors

    try {
        const account = instance.getAllAccounts()[0];
        if (!account) return;

        const request = { scopes: ["https://watertronics-my.sharepoint.com/.default"], account };
        const tokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = tokenResponse.accessToken;

        const siteUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL;
        const listName = process.env.REACT_APP_QAFEEDBACK_LIST_NAME;

        // Fetch attachments
        const attachmentRes = await fetch(
            `${siteUrl}/_api/web/lists/getbytitle('${listName}')/items(${item.id})/AttachmentFiles`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: "application/json;odata=nometadata"
                }
            }
        );

        if (!attachmentRes.ok) {
            throw new Error(`Failed to fetch attachments: ${attachmentRes.status}`);
        }

        const attachmentData = await attachmentRes.json();
        console.log("Fetched Attachments:", attachmentData.value);

        // Ensure attachments are properly set
        setAttachments(Array.isArray(attachmentData.value) ? attachmentData.value : []);
    } catch (error) {
        console.error("Error fetching attachments:", error);
        setAttachments([]); // Ensure it's always an array
    }
};

  const fetchUserAndItems = useCallback(async () => {
    const requestId = ++loadRequest.current;
    setMatchingItems([]);
    setLoadError('');
    if (!Number.isSafeInteger(Number(jobId)) || Number(jobId) <= 0) return;
    try {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
      if (!account) return;
      const token = await instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/.default"], account,
      });
      const items = await fetchQAFeedbackForProject(jobId, token.accessToken);
      if (requestId === loadRequest.current) setMatchingItems(items);
    } catch (error) {
      if (requestId === loadRequest.current) setLoadError('Unable to load QA feedback. Please reload and try again.');
      console.error("Error fetching QA feedback:", error);
    }
  }, [instance, jobId]);

  useEffect(() => {
    fetchUserAndItems();
    return () => { loadRequest.current += 1; };
  }, [fetchUserAndItems]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate JobNumber and JobName before proceeding
    const jobNumber = projectDetails[0]?.JobNumber;
    const jobName = projectDetails[0]?.ProjectName;
    
    if (!jobNumber || jobNumber === "" || !jobName || jobName === "") {
        alert("Missing job information. Please refresh the page and try again.");
        return;
    }
    
    try {
        setIsSubmitting(true); // Start loading state
        setAttachments([]); // Reset before processing

        const account = instance.getAllAccounts()[0];
        if (!account) {
            setIsSubmitting(false);
            return;
        }

        const request = { scopes: ["https://watertronics-my.sharepoint.com/.default"], account };
        const tokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = tokenResponse.accessToken;

        const siteUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL;
        const listName = process.env.REACT_APP_QAFEEDBACK_LIST_NAME;

        // Fetch correct metadata type for SharePoint
        const metadataRes = await axios.get(
            `${siteUrl}/_api/web/lists/getbytitle('${listName}')?$select=ListItemEntityTypeFullName`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: "application/json;odata=verbose"
                }
            }
        );        const metadata = metadataRes.data;
        const listItemEntityType = metadata.d.ListItemEntityTypeFullName;

        // Get engineer values for debugging
        const mechanicalEngineerValue = projectDetails[0]?.MechanicalEngineer || "";
        const electricalEngineerValue = projectDetails[0]?.ElectricalEngineer || "";
        
        console.log("=== ENGINEER DEBUG INFO ===");
        console.log("Project Details:", projectDetails[0]);
        console.log("Mechanical Engineer Value:", mechanicalEngineerValue);
        console.log("Electrical Engineer Value:", electricalEngineerValue);

        // Create payload
        const payload = {
            __metadata: { type: listItemEntityType },
            Title: subject,
            Comments: description,
            JobNumber: projectDetails[0]?.JobNumber || "",
            JobName: projectDetails[0]?.ProjectName || "",
            SaleQuoteID: projectDetails[0]?.SalesQuoteID || null,
            Mechanical: mechanicalEngineerValue,
            Electrical: electricalEngineerValue,
            ProjectID: jobId || null
        };


        // Create a new SharePoint list item
        const response = await axios.post(
            `${siteUrl}/_api/web/lists/getbytitle('${listName}')/items`,
            payload,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: "application/json;odata=verbose",
                    "Content-Type": "application/json;odata=verbose"
                }
            }        );
        
        const newItem = response.data;

        // Upload attachments if files exist
        if (files.length > 0) {
            const itemId = newItem.d.ID;

            for (const file of files) {
                const fileName = encodeURIComponent(file.name);

                const uploadResponse = await fetch(
                    `${siteUrl}/_api/web/lists/getbytitle('${listName}')/items(${itemId})/AttachmentFiles/add(FileName='${fileName}')`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            Accept: "application/json;odata=verbose"
                        },
                        body: file
                    }
                );

                if (!uploadResponse.ok) {
                    const errorDetails = await uploadResponse.text();
                    throw new Error(`Error uploading file: ${uploadResponse.status} - ${errorDetails}`);
                }
            }
        }

        // Reset form and attachments
        setSubject("");
        setDescription("");
        setFiles([]); // Reset files array
        setAttachments([]); // ✅ Ensures attachments are reset
        document.getElementById("file").value = ""; // Clear the file input field

        // Add a small delay to allow SharePoint to finish processing
        // This ensures that when we reload, we get the complete item with proper metadata
        setTimeout(async () => {
            // Reload the component after all operations are complete
            await fetchUserAndItems();
            setIsSubmitting(false); // End loading state
        }, 1500); // 1.5 second delay
    } catch (error) {
        console.error("Error submitting form:", error);
        setIsSubmitting(false); // Make sure to reset loading state on error
    }
};



  return (
    <div>
      <TopNav />
      <JobGeneralOverview />
      <JobNav keyValue="jobQa" />
      <div className="container">
        <h2 className="mt-4 text-center">Quality Assurance Feedback</h2>
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              className="form-control"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              className="form-control"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="file">Attachments</label>
            <input
              type="file"
              className="form-control"
              id="file"
              multiple // Allow multiple files
              onChange={(e) => setFiles(Array.from(e.target.files))}
            />
          </div>          <button 
            type="submit" 
            className="btn btn-primary mt-2" 
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
        
        {isSubmitting && (
          <div className="text-center my-3">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only"></span>
            </div>
            <p className="mt-2">Saving your feedback...</p>
          </div>
        )}
        
        {loadError && <p role="alert" className="text-danger">{loadError}</p>}
        {!isSubmitting && matchingItems.length === 0 ? (
          <p>No quality assurance feedback found{jobNumber ? " for this job" : ""}.</p>
        ) : !isSubmitting && (
          <div className="table-responsive">
            <table className="table table-bordered">              <thead className="thead-dark">
                <tr>
                  <th>Title</th>
                  <th>Created Date</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {matchingItems.map((item) => (
                  <tr key={item.id} onClick={() => handleShow(item)}>                    <td>
                      {item.fields?.Attachments && (
                        <span className="ml-2">
                          <i className="bi bi-paperclip" />
                        </span>
                      )}
                      {item.fields?.Title}
                    </td>
                    <td>
                      {new Date(item.createdDateTime).toLocaleDateString()}
                    </td>
                    <td>{item.createdBy?.user.displayName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Item Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
    {selectedItem && (
        <form>
            <div className="form-group">
                <label htmlFor="modalTitle">Title</label>
                <input
                    type="text"
                    className="form-control"
                    id="modalTitle"
                    value={selectedItem.fields?.Title || ""}
                    disabled
                />
            </div>
            <div className="form-group">
                <label htmlFor="modalComments">Comments</label>
                <textarea
                    className="form-control"
                    rows={6}
                    id="modalComments"
                    value={selectedItem.fields?.Comments || ""}
                    disabled
                />            </div>
            <div className="form-group">
                <label htmlFor="modalMechEngineer">Mechanical Engineer</label>
                <input
                    type="text"
                    className="form-control"
                    id="modalMechEngineer"
                    value={selectedItem.fields?.MechanicalEngineer || ""}
                    disabled
                />
            </div>
            <div className="form-group">
                <label htmlFor="modalElecEngineer">Electrical Engineer</label>
                <input
                    type="text"
                    className="form-control"
                    id="modalElecEngineer"
                    value={selectedItem.fields?.ElectricalEngineer || ""}
                    disabled
                />
            </div>
            {attachments && attachments.length > 0 ? (
                <div className="form-group">
                    <label>Attachments</label>
                    <ul>
                        {attachments.map((file, index) => {
                            let serverRelativeUrl = file.ServerRelativeUrl || "";

                            // Ensure no duplicate "/sites/businessmanagementsystem/"
                            if (serverRelativeUrl.startsWith("/sites/businessmanagementsystem/sites/")) {
                                serverRelativeUrl = serverRelativeUrl.replace(
                                    "/sites/businessmanagementsystem/sites/",
                                    "/sites/businessmanagementsystem/"
                                );
                            }

                            const fullUrl = `https://watertronics.sharepoint.com${serverRelativeUrl}`;

                            return (
                                <li key={index}>
                                    <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                                        {file.FileName || "Unnamed File"}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : (
                <p>No attachments available</p> // ✅ Prevents undefined error
            )}
        </form>
    )}
</Modal.Body>


        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JobQa;
